<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { companyIdentity } from '@jws/shared';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import jwsLogo from './assets/jws-logo.gif';
import { getBootstrap, type Contact } from './api';
import { isZh, locale, text, toggleLocale } from './i18n';
import { applyDocumentSeo } from './seo';

const menuOpen = ref(false);
const siteContact = ref<Contact | null>(null);
const route = useRoute();
let seoRequest = 0;

const primaryChannels = computed(() => {
  const channels = siteContact.value?.channels ?? [];
  const selected = channels.filter((channel) => channel.isPrimary);
  return (selected.length ? selected : channels).slice(0, 3);
});
const utilityChannels = computed(() => primaryChannels.value.slice(0, 2));
const siteCompanyName = computed(() => siteContact.value?.companyName || text(companyIdentity.zh.name, companyIdentity.en.name));

watch(locale, (currentLocale) => { void loadSiteContact(currentLocale); }, { immediate: true });

watch([locale, () => route.name], ([currentLocale, routeName]) => {
  if (routeName === 'product-detail' || routeName === 'news-detail' || routeName === 'case-detail' || routeName === 'page') return;
  void applySiteSeo(currentLocale);
}, { immediate: true });

async function applySiteSeo(currentLocale: 'zh-CN' | 'en-US') {
  const requestId = ++seoRequest;
  try {
    const bootstrap = await getBootstrap(currentLocale);
    if (requestId !== seoRequest) return;
    const value = bootstrap.settings['site.seo'];
    if (!isSeoSettings(value)) return;
    const setting = currentLocale === 'zh-CN' ? value.zh : value.en;
    applyDocumentSeo(setting.title, setting.description);
  } catch {
    // 公开站点的内容加载不应被站点级 SEO 请求阻断。
  }
}

async function loadSiteContact(currentLocale: 'zh-CN' | 'en-US') {
  try {
    siteContact.value = (await getBootstrap(currentLocale)).contact;
  } catch {
    siteContact.value = null;
  }
}

function channelHref(channel: Contact['channels'][number]): string | null {
  if (channel.href) return channel.href;
  if (channel.type === 'phone') return `tel:${channel.value.replace(/[^+\d]/g, '')}`;
  if (channel.type === 'email') return `mailto:${channel.value}`;
  if ((channel.type === 'social' || channel.type === 'website') && /^https?:\/\//i.test(channel.value)) return channel.value;
  return null;
}

function isExternalHref(href: string | null): boolean { return Boolean(href && /^https?:\/\//i.test(href)); }

function isSeoSettings(value: unknown): value is { zh: SeoLocale; en: SeoLocale } {
  return Boolean(value) && typeof value === 'object'
    && isSeoLocale((value as { zh?: unknown }).zh)
    && isSeoLocale((value as { en?: unknown }).en);
}

type SeoLocale = { title: string; description: string | null; keywords: string | null };
function isSeoLocale(value: unknown): value is SeoLocale {
  return Boolean(value) && typeof value === 'object' && typeof (value as { title?: unknown }).title === 'string';
}

</script>

<template>
  <div class="site-shell">
    <header class="site-header">
      <div class="site-utility">
        <div class="container site-utility__inner">
          <span>{{ siteCompanyName }}</span>
          <div class="site-utility__contacts">
            <template v-for="channel in utilityChannels" :key="`${channel.type}-${channel.label}-${channel.value}`"><a v-if="channelHref(channel)" :href="channelHref(channel) || undefined" :target="isExternalHref(channelHref(channel)) ? '_blank' : undefined" :rel="isExternalHref(channelHref(channel)) ? 'noreferrer' : undefined">{{ channel.value }}</a><span v-else>{{ channel.value }}</span></template>
          </div>
        </div>
      </div>
      <div class="site-header__nav">
        <div class="container site-header__inner">
          <RouterLink
            class="site-brand"
            to="/"
            :aria-label="text('技卫士首页', 'JWS home')"
            @click="menuOpen = false"
          >
            <img
              :src="jwsLogo"
              alt="技卫士"
              width="220"
              height="60"
            >
          </RouterLink>
          <button
            class="menu-button"
            type="button"
            :aria-expanded="menuOpen"
            aria-controls="primary-nav"
            @click="menuOpen = !menuOpen"
          >
            <span /><span /><span /><b class="sr-only">{{ text('打开导航', 'Open navigation') }}</b>
          </button>
          <div
            id="primary-nav"
            class="site-header__panel"
            :class="{ 'is-open': menuOpen }"
          >
            <nav
              class="primary-nav"
              :aria-label="text('主导航', 'Primary navigation')"
              @click="menuOpen = false"
            >
              <RouterLink to="/">
                {{ text('首页', 'Home') }}
              </RouterLink>
              <RouterLink to="/products">
                {{ text('产品中心', 'Products') }}
              </RouterLink>
              <RouterLink to="/cases">
                {{ text('行业案例', 'Projects') }}
              </RouterLink>
              <RouterLink to="/news">
                {{ text('新闻资讯', 'News') }}
              </RouterLink>
              <RouterLink to="/company/company">
                {{ text('关于我们', 'Company') }}
              </RouterLink>
              <RouterLink to="/contact">
                {{ text('联系我们', 'Contact') }}
              </RouterLink>
            </nav>
            <div class="site-header__actions">
              <button
                type="button"
                class="locale-button"
                :aria-label="text('切换到英文', 'Switch to Chinese')"
                @click="toggleLocale"
              >
                <span :class="{ 'is-active': isZh }">中文</span>
                <i aria-hidden="true" />
                <span :class="{ 'is-active': !isZh }">EN</span>
              </button>
              <RouterLink
                class="header-inquiry"
                to="/contact"
                @click="menuOpen = false"
              >
                {{ text('项目咨询', 'Inquiry') }}
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main><RouterView /></main>

    <footer class="site-footer">
      <div class="container footer-grid">
        <div>
          <RouterLink
            class="footer-brand"
            to="/"
            :aria-label="text('技卫士首页', 'JWS home')"
          >
            <img
              :src="jwsLogo"
              alt="技卫士"
              width="220"
              height="60"
            >
          </RouterLink>
          <p>{{ text('围绕人体安检设备、软件系统与项目交付，服务司法监所、机场海关、矿山与重点设施。', 'People-screening equipment, software and project delivery for corrections, transport, mining and critical facilities.') }}</p>
        </div>
        <div>
          <h2>{{ text('产品与项目', 'Products and projects') }}</h2>
          <RouterLink to="/products">
            {{ text('产品中心', 'Products') }}
          </RouterLink>
          <RouterLink to="/cases">
            {{ text('行业案例', 'Projects') }}
          </RouterLink>
          <RouterLink to="/news">
            {{ text('新闻资讯', 'News') }}
          </RouterLink>
        </div>
        <div>
          <h2>{{ text('公司信息', 'Company') }}</h2>
          <RouterLink to="/company/company">
            {{ text('公司简介', 'Company profile') }}
          </RouterLink>
          <RouterLink to="/company/factory">
            {{ text('研发制造', 'R&D and manufacturing') }}
          </RouterLink>
          <RouterLink to="/contact">
            {{ text('联系我们', 'Contact') }}
          </RouterLink>
        </div>
        <div>
          <h2>{{ text('业务联系', 'Business contact') }}</h2>
          <template v-for="channel in primaryChannels" :key="`${channel.type}-${channel.label}-${channel.value}`"><a v-if="channelHref(channel)" :href="channelHref(channel) || undefined" :target="isExternalHref(channelHref(channel)) ? '_blank' : undefined" :rel="isExternalHref(channelHref(channel)) ? 'noreferrer' : undefined">{{ channel.value }}</a><span v-else>{{ channel.value }}</span></template>
          <RouterLink
            class="footer-cta"
            to="/contact"
          >
            {{ text('提交项目需求 →', 'Submit a project inquiry →') }}
          </RouterLink>
        </div>
      </div>
      <div class="container footer-bottom">
        <span>© {{ new Date().getFullYear() }} {{ siteCompanyName }} {{ text('保留所有权利。', 'All rights reserved.') }}</span>
        <span>{{ text('人体安检系统与解决方案', 'People screening systems and solutions') }}</span>
      </div>
    </footer>
  </div>
</template>
